import React from "react";
import { Card, Descriptions, Button, Space, List, Empty, Tag, Table } from "antd";
import {
  FileTextOutlined,
  EnvironmentOutlined,
  AimOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { IncidenceAuditDetail } from "../../../types";
import dayjs from "dayjs";
import { getMediaUrl } from "../../../utils/mediaUrl";

interface AuditInfoPanelProps {
  audit: IncidenceAuditDetail;
}

/**
 * AuditInfoPanel Component
 * Displays comprehensive audit information including:
 * - Basic audit details (number, date, status, producer)
 * - Audit act document (if available)
 * - Farm map (if available)
 * - GPS track (if available)
 * - Control points list (if available)
 */
export const AuditInfoPanel: React.FC<AuditInfoPanelProps> = ({ audit }) => {
  const { t, i18n } = useTranslation();

  if (!audit) {
    return (
      <Empty
        description={t(
          "incidence.no_audit_info",
          "No hay información de auditoría disponible"
        )}
      />
    );
  }

  // Format number according to current locale
  const formatNumber = (value: number | null | undefined, decimals: number = 2): string => {
    if (value === null || value === undefined) return "-";
    return value.toLocaleString(i18n.language, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  // Calculate total kg from plots visited
  const calculateTotalKg = (): number => {
    if (!audit.plots_visited || audit.plots_visited.length === 0) return 0;
    return audit.plots_visited.reduce((total, plot) => total + (plot.kg_plot || 0), 0);
  };

  // Get audit status display
  const getAuditStatusDisplay = (status?: string | null): string => {
    if (!status) return "-";
    // Map audit status codes to display names
    const statusMap: Record<string, string> = {
      'not_planned': t("audit.status.not_planned", "No Planificada"),
      'planned': t("audit.status.planned", "Planificada"),
      'performed': t("audit.status.performed", "Realizada"),
      'finalized': t("audit.status.finalized", "Finalizada"),
      'canceled': t("audit.status.canceled", "Cancelada"),
    };
    return statusMap[status] || status;
  };

  // Get audit status color
  const getAuditStatusColor = (status?: string | null): string => {
    if (!status) return "default";
    // Map audit status codes to colors
    const colorMap: Record<string, string> = {
      'not_planned': "default",
      'planned': "blue",
      'performed': "orange",
      'finalized': "green",
      'canceled': "red",
    };
    return colorMap[status] || "default";
  };

  // Get audit type display
  const getAuditTypeDisplay = (type?: number | null): string => {
    if (!type) return "-";
    // Map audit type codes to display names
    const typeMap: Record<number, string> = {
      1: "Aforo",
      2: "Completa",
      3: "Seguimiento",
    };
    return typeMap[type] || `Tipo ${type}`;
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      {/* Basic Audit Information */}
      <Card 
        title={t("audit.information", "Información de la Auditoría")}
        extra={
          audit.has_audit_act && audit.audit_act_url && (
            <a
              href={getMediaUrl(audit.audit_act_url) || audit.audit_act_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <Tag color="blue" style={{ cursor: 'pointer' }}>
                <FileTextOutlined style={{ marginRight: '4px' }} />
                {t("audit.act", "Acta de la auditoría")}
              </Tag>
            </a>
          )
        }
      >
        <Descriptions column={3} bordered>
          {/* Fila 1: Referencia, Estado */}
          <Descriptions.Item
            label={t("audit.reference", "Referencia")}
            span={1}
          >
            <strong>{audit.reference}</strong>
          </Descriptions.Item>
          <Descriptions.Item
            label={t("audit.status_label", "Estado")}
            span={2}
          >
            <Tag color={getAuditStatusColor(audit.status_of_audit)}>
              {audit.status_name || getAuditStatusDisplay(audit.status_of_audit)}
            </Tag>
          </Descriptions.Item>

          {/* Fila 2: Nombre Finca, Municipio (Provincia), Tipo de auditoría */}
          <Descriptions.Item
            label={t("farm.name", "Nombre Finca")}
            span={1}
          >
            {audit.farm?.name || "-"}
          </Descriptions.Item>
          <Descriptions.Item
            label={t("farm.location", "Municipio (Provincia)")}
            span={1}
          >
            {audit.farm?.town && audit.farm?.province
              ? `${audit.farm.town} (${audit.farm.province})`
              : audit.farm?.town || audit.farm?.province || "-"}
          </Descriptions.Item>
          <Descriptions.Item
            label={t("audit.type", "Tipo de auditoría")}
            span={1}
          >
            {audit.type_name || getAuditTypeDisplay(audit.type_of_audit)}
          </Descriptions.Item>

          {/* Fila 3: Campaña, Auditor, Fecha Inicio (Fecha Planificada) */}
          <Descriptions.Item
            label={t("audit.season", "Campaña")}
            span={1}
          >
            {audit.season || "-"}
          </Descriptions.Item>
          <Descriptions.Item
            label={t("audit.auditor", "Auditor")}
            span={1}
          >
            {audit.auditor || "-"}
          </Descriptions.Item>
          <Descriptions.Item
            label={t("audit.dates", "Fecha Inicio (Fecha Planificada)")}
            span={1}
          >
            {audit.start_date
              ? dayjs(audit.start_date).format("DD/MM/YYYY")
              : "-"}
            {audit.planning_date && audit.planning_date !== audit.start_date && (
              <span style={{ color: "#666", marginLeft: "4px" }}>
                ({dayjs(audit.planning_date).format("DD/MM/YYYY")})
              </span>
            )}
          </Descriptions.Item>

          {/* Fila 4: Hora Inicio / Fin, Superficie Finca, Superficie auditada */}
          <Descriptions.Item
            label={t("audit.time", "Hora Inicio / Fin")}
            span={1}
          >
            {audit.start_time || audit.finishing_time
              ? `${audit.start_time ? audit.start_time.substring(0, 5) : "-"} / ${audit.finishing_time ? audit.finishing_time.substring(0, 5) : "-"}`
              : "-"}
          </Descriptions.Item>
          <Descriptions.Item
            label={t("audit.farm_surface", "Superficie Finca")}
            span={1}
          >
            {audit.farm_surface ? `${formatNumber(audit.farm_surface)} ha` : "-"}
          </Descriptions.Item>
          <Descriptions.Item
            label={t("audit.audited_surface", "Superficie auditada")}
            span={1}
          >
            {audit.audited_surface ? `${formatNumber(audit.audited_surface)} ha` : "-"}
          </Descriptions.Item>

          {/* Fila 5: Muestras tomadas */}
          <Descriptions.Item
            label={t("audit.samples_taken", "Muestras tomadas")}
            span={3}
          >
            {audit.samples_taken !== undefined && audit.samples_taken !== null ? audit.samples_taken : 0}
          </Descriptions.Item>

          {/* Fila 6: Observaciones */}
          {audit.comments && (
            <Descriptions.Item
              label={t("audit.comments", "Observaciones")}
              span={3}
            >
              {audit.comments}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* Production Information - Moved right after Audit Information */}
      <Card title={t("audit.production_info", "Información de Producción")}>
        <Descriptions column={3} bordered>
          {/* Fila 1: Etiquetador, Exportadores */}
          <Descriptions.Item
            label={t("audit.packers", "Etiquetador")}
            span={1}
          >
            {audit.packers && audit.packers.length > 0
              ? audit.packers.map(p => p.name).join(", ")
              : "-"}
          </Descriptions.Item>
          <Descriptions.Item
            label={t("audit.exporters", "Exportadores")}
            span={2}
          >
            {audit.exporters && audit.exporters.length > 0
              ? audit.exporters.map(e => e.name).join(", ")
              : "-"}
          </Descriptions.Item>

          {/* Fila 2: Producción estimada del productor, Aforo Estimado del auditor, Fecha estimada de corta */}
          <Descriptions.Item
            label={t("audit.stimated_production", "Producción estimada por productor")}
            span={1}
          >
            {audit.stimated_production ? `${formatNumber(audit.stimated_production, 0)} kg` : "-"}
          </Descriptions.Item>
          <Descriptions.Item
            label={t("audit.total_kg", "Aforo estimado por auditor")}
            span={1}
          >
            {formatNumber(calculateTotalKg(), 0)} kg
          </Descriptions.Item>
          <Descriptions.Item
            label={t("audit.harvest_date", "Fecha estimada de corta")}
            span={1}
          >
            {audit.harvest_date
              ? dayjs(audit.harvest_date).format("DD/MM/YYYY")
              : "-"}
          </Descriptions.Item>

          {/* Fila 3: Tipo de producción, Finca mallada, % de merma */}
          <Descriptions.Item
            label={t("audit.production_type", "Tipo de producción")}
            span={1}
          >
            {audit.production_type || "-"}
          </Descriptions.Item>
          <Descriptions.Item
            label={t("audit.is_under_net", "Bajo malla")}
            span={1}
          >
            {audit.is_under_net || "-"}
          </Descriptions.Item>
          <Descriptions.Item
            label={t("audit.decrease_percentage", "Porcentaje de merma")}
            span={1}
          >
            {audit.decrease_percentage !== undefined && audit.decrease_percentage !== null 
              ? `${audit.decrease_percentage}%` 
              : "-"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Farm Map */}
      {audit.farm_map && (
        <Card title={t("audit.farm_map", "Mapa de la Finca")}>
          <div style={{ padding: "16px", textAlign: "center" }}>
            <p>
              {t(
                "audit.map_available",
                "Mapa de la finca disponible (geometría GeoJSON)"
              )}
            </p>
            <Button
              type="default"
              icon={<EnvironmentOutlined />}
              onClick={() => {
                // TODO: Implement map viewer
              }}
            >
              {t("audit.view_map", "Ver Mapa")}
            </Button>
          </div>
        </Card>
      )}

      {/* GPS Track */}
      {audit.gps_track && (
        <Card title={t("audit.gps_track", "Track GPS")}>
          <Space>
            <Button
              type="primary"
              icon={<EnvironmentOutlined />}
              onClick={() => {
                // TODO: Implement GPS track viewer
              }}
            >
              {t("audit.view_gps_track", "Ver Track GPS")}
            </Button>
          </Space>
        </Card>
      )}

      {/* Plots Visited */}
      {audit.plots_visited && audit.plots_visited.length > 0 ? (
        <Card
          title={t("audit.plots_visited", "Parcelas Visitadas")}
          extra={
            <span>
              {t("common.total", "Total")}: {audit.plots_visited.length}
            </span>
          }
        >
          <Table
            dataSource={audit.plots_visited}
            rowKey="id"
            pagination={false}
            size="small"
            summary={(pageData) => {
              let totalSurface = 0;
              let totalPlants = 0;
              let totalKg = 0;

              pageData.forEach(({ surface, plants, kg_plot }) => {
                totalSurface += surface || 0;
                totalPlants += plants || 0;
                totalKg += kg_plot || 0;
              });

              return (
                <Table.Summary.Row style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>
                  <Table.Summary.Cell index={0}>
                    {t("common.total", "Total")}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    {formatNumber(totalSurface)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    {formatNumber(totalPlants)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>
                    -
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4}>
                    -
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5}>
                    {formatNumber(totalKg)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
            columns={[
              {
                title: t("plot.name", "Nombre"),
                dataIndex: "name",
                key: "name",
                width: "15%",
                sorter: (a: any, b: any) => a.name.localeCompare(b.name),
              },
              {
                title: t("plot.surface", "Superficie (ha)"),
                dataIndex: "surface",
                key: "surface",
                width: "12%",
                sorter: (a: any, b: any) => (a.surface || 0) - (b.surface || 0),
                render: (surface: number) => surface ? formatNumber(surface) : "-",
              },
              {
                title: t("plot.plants", "Plantas"),
                dataIndex: "plants",
                key: "plants",
                width: "10%",
                sorter: (a: any, b: any) => (a.plants || 0) - (b.plants || 0),
                render: (plants: number) => plants ? formatNumber(plants) : "-",
              },
              {
                title: t("plot.plantation_frame", "Marco"),
                key: "plantation_frame",
                width: "12%",
                render: (_: any, record: any) => {
                  if (record.plantation_frame_row && record.plantation_frame_column) {
                    return `${record.plantation_frame_row} x ${record.plantation_frame_column}`;
                  }
                  return "-";
                },
              },
              {
                title: t("plot.pattern", "Patrón"),
                dataIndex: "pattern_name",
                key: "pattern_name",
                width: "18%",
                sorter: (a: any, b: any) => (a.pattern_name || "").localeCompare(b.pattern_name || ""),
                render: (pattern_name: string) => pattern_name || "-",
              },
              {
                title: t("plot.kg_plot", "Aforo estimado (kg)"),
                dataIndex: "kg_plot",
                key: "kg_plot",
                width: "15%",
                sorter: (a: any, b: any) => (a.kg_plot || 0) - (b.kg_plot || 0),
                render: (kg_plot: number) => kg_plot ? formatNumber(kg_plot) : "-",
              },
            ]}
          />
        </Card>
      ) : (
        <Card title={t("audit.plots_visited", "Parcelas Visitadas")}>
          <Empty
            description={t(
              "audit.no_plots_visited",
              "No hay parcelas visitadas registradas para esta auditoría"
            )}
          />
        </Card>
      )}

      {/* Control Points */}
      {audit.control_points && audit.control_points.length > 0 && (
        <Card
          title={t("audit.control_points", "Puntos de Control")}
          extra={
            <span>
              {t("common.total", "Total")}: {audit.control_points.length}
            </span>
          }
        >
          <List
            dataSource={audit.control_points}
            renderItem={(point) => (
              <List.Item>
                <List.Item.Meta
                  avatar={<AimOutlined style={{ fontSize: "24px" }} />}
                  title={
                    point.waypoint_name ||
                    point.name ||
                    `${t("control_point.point", "Punto")} ${point.id}`
                  }
                  description={
                    <Space direction="vertical" size="small">
                      {point.status && (
                        <span>
                          <strong>{t("control_point.status", "Estado")}:</strong>{" "}
                          {point.status}
                        </span>
                      )}
                      {point.reason_name && (
                        <span>
                          <strong>{t("control_point.reason", "Motivo")}:</strong>{" "}
                          {point.reason_name}
                        </span>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* No control points message */}
      {(!audit.control_points || audit.control_points.length === 0) && (
        <Card title={t("audit.control_points", "Puntos de Control")}>
          <Empty
            description={t(
              "audit.no_control_points",
              "No hay puntos de control registrados para esta auditoría"
            )}
          />
        </Card>
      )}

      {/* Samples */}
      {audit.samples && audit.samples.length > 0 && (
        <Card
          title={t("audit.samples", "Muestras")}
          extra={
            <span>
              {t("common.total", "Total")}: {audit.samples.length}
            </span>
          }
        >
          <List
            dataSource={audit.samples}
            renderItem={(sample) => (
              <List.Item>
                <List.Item.Meta
                  title={sample.code || `Muestra ${sample.id}`}
                  description={
                    sample.state && (
                      <span>
                        <strong>{t("sample.state", "Estado")}:</strong>{" "}
                        {sample.state}
                      </span>
                    )
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* No samples message */}
      {(!audit.samples || audit.samples.length === 0) && (
        <Card title={t("audit.samples", "Muestras")}>
          <Empty
            description={t(
              "audit.no_samples",
              "No hay muestras registradas para esta auditoría"
            )}
          />
        </Card>
      )}
    </Space>
  );
};
